// FILE: src/App.js

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Import only client-side components/pages
import NavBar from "./components/NavBar";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import ManageUsersPage from "./pages/ManageUsersPage";
import CashAccountsPage from "./pages/CashAccountsPage";
import CategoriesPage from "./pages/CategoriesPage";
import TransactionPage from "./pages/TransactionPage";
import LedgerPage from "./pages/LedgerPage";
import ReconciliationListPage from "./pages/ReconciliationListPage";
import ReconciliationFormPage from "./pages/ReconciliationFormPage";
import ReconciliationReopenPage from "./pages/ReconciliationReopenPage";
import ReconciliationReFinalizePage from "./pages/ReconciliationReFinalizePage";
import ZReportListPage from "./pages/ZReportListPage";
import ZReportExpectedPage from "./pages/ZReportExpectedPage";
import ZReportActualPage from "./pages/ZReportActualPage";
import DashboardPage from "./pages/DashboardPage";
import AccountDetailPage from "./pages/AccountDetailPage";
import ZReportDetailPage from "./pages/ZReportDetailPage";
import DetailedReportPage from "./pages/DetailedReportPage";
import EditZReportExpectedPage from "./pages/EditZReportExpectedPage";
import ProfilePage from "./pages/ProfilePage";
import AuditLogPage from "./pages/AuditLogPage";
import ReconciliationSettingsPage from "./pages/ReconciliationSettingsPage";
import ShiftsPage from "./pages/ShiftsPage";

// Advanced Report pages
import AdvanceReportPage from "./pages/AdvanceReportPage";
import TillVarianceReportPage from "./pages/TillVarianceReportPage";
import C2BVarianceReportPage from "./pages/C2BVarianceReportPage";
import CashinReportPage from "./pages/CashinReportPage";
import CashoutReportPage from "./pages/CashoutReportPage";
import CashsalesReportPage from "./pages/CashsalesReportPage";
import DriverWageReportPage from "./pages/DriverWageReportPage";
import CategoryReportPage from "./pages/CategoryReportPage";
import IssueReportPage from "./pages/IssueReportPage";
import ReconciliationReportPage from "./pages/ReconciliationReportPage";
import ExpectedC2BReportPage from "./pages/ExpectedC2BReportPage";
import TargetCashReportPage from "./pages/TargetCashReportPage";
import DeclaredCashReportPage from "./pages/DeclaredCashReportPage";
// Manage Till User page
import ManagetilluserPage from "./pages/ManageTillUserPage";

import ResetPasswordPage from "./pages/ResetPasswordPage";
import BatchShiftCreate from "./pages/BatchShiftCreate";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles.css";

// Utility function to check authentication (client-only)
const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/auth" replace />;
};

// Role-restricted route: requires auth + allowed role
const RoleRoute = ({ children, blockedRoles = [] }) => {
  if (!isAuthenticated()) return <Navigate to="/auth" replace />;
  const role = localStorage.getItem("userRole");
  if (blockedRoles.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
};

// Layout component that conditionally renders NavBar based on the current route
function Layout() {
  const location = useLocation();
  // Hide the NavBar on the public marketing/auth screens (landing + auth)
  const hideNavBar = location.pathname === "/auth" || location.pathname === "/";

  return (
    <>
      {!hideNavBar && <NavBar />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route
          path="/manage-users"
          element={
            <ProtectedRoute>
              <ManageUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-accounts"
          element={
            <ProtectedRoute>
              <CashAccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <CategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <TransactionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliations"
          element={
            <ProtectedRoute>
              <ReconciliationListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts"
          element={
            <ProtectedRoute>
              <ShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation/new"
          element={
            <ProtectedRoute>
              <ReconciliationFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation/edit/:id"
          element={
            <ProtectedRoute>
              <ReconciliationFormPage />
            </ProtectedRoute>
          }
        />
        <Route path="/detailed-report" element={<DetailedReportPage />} />
        <Route
          path="/reconciliation/reopen/:id"
          element={
            <ProtectedRoute>
              <ReconciliationReopenPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation/refinalize/:accountId"
          element={
            <ProtectedRoute>
              <ReconciliationReFinalizePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zreport-list"
          element={
            <RoleRoute blockedRoles={["Accountant"]}>
              <ZReportListPage />
            </RoleRoute>
          }
        />
        <Route
          path="/zreport-expected"
          element={
            <RoleRoute blockedRoles={["Accountant"]}>
              <ZReportExpectedPage />
            </RoleRoute>
          }
        />
        <Route
          path="/zreport-actual"
          element={
            <RoleRoute blockedRoles={["Accountant"]}>
              <ZReportActualPage />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route path="/account/detail/:id" element={<AccountDetailPage />} />
        <Route
          path="/zreport-details/:zReportId"
          element={
            <RoleRoute blockedRoles={["Accountant"]}>
              <ZReportDetailPage />
            </RoleRoute>
          }
        />
        <Route
          path="/ledger"
          element={
            <ProtectedRoute>
              <LedgerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zreport-edit/:id"
          element={
            <RoleRoute blockedRoles={["Accountant"]}>
              <EditZReportExpectedPage />
            </RoleRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation-settings"
          element={
            <ProtectedRoute>
              <ReconciliationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-tilluser"
          element={
            <ProtectedRoute>
              <ManagetilluserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports"
          element={
            <ProtectedRoute>
              <AdvanceReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/till-variance"
          element={
            <ProtectedRoute>
              <TillVarianceReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/c2b-variance"
          element={
            <ProtectedRoute>
              <C2BVarianceReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/cashin"
          element={
            <ProtectedRoute>
              <CashinReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/cashout"
          element={
            <ProtectedRoute>
              <CashoutReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/cashsales"
          element={
            <ProtectedRoute>
              <CashsalesReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/driver-wage"
          element={
            <ProtectedRoute>
              <DriverWageReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/issue"
          element={
            <ProtectedRoute>
              <IssueReportPage />
            </ProtectedRoute>
          }
        />
        <Route
        path="/advance-reports/category-report"
        element={
          <ProtectedRoute>
            <CategoryReportPage />
          </ProtectedRoute>
        } />
        <Route
          path="/advance-reports/reconciliation-report"
          element={
            <ProtectedRoute>
              <ReconciliationReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/expected-c2b"
          element={
            <ProtectedRoute>
              <ExpectedC2BReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/target-cash"
          element={
            <ProtectedRoute>
              <TargetCashReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advance-reports/declared-cash"
          element={
            <ProtectedRoute>
              <DeclaredCashReportPage />
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/shifts/batch-create" element={<BatchShiftCreate />} />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </>
  );
}

function App() {
  return (
    <Router>
      <Layout />
    </Router>
  );
}

export default App;
